import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productsService } from '../../services/products.service';
import { 
  Product, 
  CreateProductData, 
  UpdateProductData,
  ProductParams 
} from '../../types/products.types';

interface ProductsState {
  items: Product[];
  currentProduct: Product | null;
  loading: boolean;
  error: string | null;
  actionLoading: boolean;
  totalItems: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

const initialState: ProductsState = {
  items: [],
  currentProduct: null,
  loading: false,
  error: null,
  actionLoading: false,
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
};

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params?: ProductParams) => {
    const response = await productsService.getProducts(params);
    if (response.success) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to fetch products');
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id: string) => {
    const response = await productsService.getById(id);
    if (response.success) {
      return response.data.product;
    }
    throw new Error(response.error?.message || 'Failed to fetch product');
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (data: CreateProductData) => {
    const response = await productsService.create(data);
    if (response.success) {
      return response.data.product;
    }
    throw new Error(response.error?.message || 'Failed to create product');
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, data }: { id: string; data: UpdateProductData }) => {
    const response = await productsService.update(id, data);
    if (response.success) {
      return response.data.product;
    }
    throw new Error(response.error?.message || 'Failed to update product');
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id: string) => {
    const response = await productsService.remove(id);
    if (response.success) {
      return id;
    }
    throw new Error(response.error?.message || 'Failed to delete product');
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProducts: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.currentPage = 1;
      state.totalPages = 1;
      state.hasMore = false;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch products
    builder.addCase(fetchProducts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.items;
      state.totalItems = action.payload.pagination.total;
      state.currentPage = action.payload.pagination.page;
      state.totalPages = action.payload.pagination.totalPages;
      state.hasMore = action.payload.pagination.page < action.payload.pagination.totalPages;
    });
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch products';
    });

    // Fetch product by ID
    builder.addCase(fetchProductById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProductById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentProduct = action.payload;
    });
    builder.addCase(fetchProductById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch product';
    });

    // Create product
    builder.addCase(createProduct.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(createProduct.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.items.unshift(action.payload);
      state.totalItems += 1;
    });
    builder.addCase(createProduct.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to create product';
    });

    // Update product
    builder.addCase(updateProduct.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      state.actionLoading = false;
      const index = state.items.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentProduct?.id === action.payload.id) {
        state.currentProduct = action.payload;
      }
    });
    builder.addCase(updateProduct.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to update product';
    });

    // Delete product
    builder.addCase(deleteProduct.pending, (state) => {
      state.actionLoading = true;
      state.error = null;
    });
    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.items = state.items.filter(p => p.id !== action.payload);
      state.totalItems -= 1;
      if (state.currentProduct?.id === action.payload) {
        state.currentProduct = null;
      }
    });
    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.error.message || 'Failed to delete product';
    });
  },
});

export const { clearProducts, clearCurrentProduct, clearError } = productsSlice.actions;
export default productsSlice.reducer;
